// src/components/forms/FormSubmission.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormGroup,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Rating,
    Chip,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Collapse,
    InputAdornment,
    Slider,
} from '@mui/material';
import { db, auth } from '../firebase/config';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    doc,
    getDoc,
    addDoc,
    collection,
    query,
    where,
    getDocs,
    serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
    HelpOutline as HintIcon,
    Info as InfoIcon,
    CloudUpload as UploadIcon,
    SentimentVeryDissatisfied,
    SentimentDissatisfied,
    SentimentSatisfied,
    SentimentSatisfiedAlt,
    SentimentVerySatisfied,
    Timer as TimerIcon,
} from '@mui/icons-material';
import logo from "./Nims_Logo.png";

const emojiIcons = {
    1: { icon: <SentimentVeryDissatisfied />, label: 'Very Dissatisfied' },
    2: { icon: <SentimentDissatisfied />, label: 'Dissatisfied' },
    3: { icon: <SentimentSatisfied />, label: 'Neutral' },
    4: { icon: <SentimentSatisfiedAlt />, label: 'Satisfied' },
    5: { icon: <SentimentVerySatisfied />, label: 'Very Satisfied' },
};

function IconContainer(props) {
    const { value, ...other } = props;
    return <span {...other}>{emojiIcons[value].icon}</span>;
}

export default function FormSubmission() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();

    const [existingSubmission, setExistingSubmission] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [responses, setResponses] = useState({});
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showHints, setShowHints] = useState({});
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!user) {
            setError('User not authenticated. Please log in.');
            setLoading(false);
            return;
        }
        fetchForm();
        checkIfAlreadySubmitted();
    }, [formId, user]);

    // Timer effect
    useEffect(() => {
        if (form?.timeLimit && timeLeft === null && form.formType === 'evaluation') {
            setTimeLeft(form.timeLimit * 60); // Convert minutes to seconds
        }
    }, [form]);

    useEffect(() => {
        if (timeLeft !== null && timeLeft > 0 && !alreadySubmitted) {
            const timer = setTimeout(() => {
                setTimeLeft(timeLeft - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (timeLeft === 0 && !alreadySubmitted) {
            handleSubmit();
        }
    }, [timeLeft, alreadySubmitted]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const formData = formDoc.data();

                // Check if form is published and active
                if (!formData.isPublished) {
                    setError('This form is not currently available');
                    return;
                }
                if (formData.status !== 'active') {
                    setError('This form is not active');
                    return;
                }

                setForm(formData);

                // Initialize responses based on form type
                const initialResponses = {};
                if (formData.formType === 'evaluation' && formData.questions) {
                    formData.questions.forEach((q, idx) => {
                        if (q.type === 'multiple-choice' || q.type === 'mcq') {
                            initialResponses[`q${idx}`] = [];
                        } else if (['rating', 'nps', 'emoji'].includes(q.type)) {
                            initialResponses[`q${idx}`] = 0;
                        } else {
                            initialResponses[`q${idx}`] = '';
                        }
                    });
                } else if (formData.formType === 'feedback' && formData.questions) {
                    formData.questions.forEach((q, idx) => {
                        if (q.type === 'checkbox') {
                            initialResponses[`q${idx}`] = [];
                        } else if (['rating', 'nps', 'emoji'].includes(q.type)) {
                            initialResponses[`q${idx}`] = 0;
                        } else {
                            initialResponses[`q${idx}`] = '';
                        }
                    });
                } else if (formData.formType === 'input-detail' && formData.fields) {
                    formData.fields.forEach((field, idx) => {
                        if (field.fieldType === 'checkbox') {
                            initialResponses[`f${idx}`] = [];
                        } else if (field.defaultValue) {
                            initialResponses[`f${idx}`] = field.defaultValue;
                        } else {
                            initialResponses[`f${idx}`] = '';
                        }
                    });
                }
                setResponses(initialResponses);
            } else {
                setError('Form not found');
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form');
        } finally {
            setLoading(false);
        }
    };

    const checkIfAlreadySubmitted = async () => {
        try {
            const q = query(
                collection(db, 'submissions'),
                where('formId', '==', formId),
                where('userId', '==', user.uid)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const submissionDoc = snapshot.docs[0].data();
                setAlreadySubmitted(true);
                setExistingSubmission(submissionDoc);
                setResponses(submissionDoc.responses || {});
            }
        } catch (err) {
            console.error('Error checking submission:', err);
        }
    };

    const handleResponseChange = (questionId, value, type) => {
        setValidationErrors(prev => ({ ...prev, [questionId]: '' }));

        if (type === 'checkbox' || type === 'multiple-choice') {
            const current = responses[questionId] || [];
            if (current.includes(value)) {
                setResponses(prev => ({
                    ...prev,
                    [questionId]: current.filter(v => v !== value)
                }));
            } else {
                setResponses(prev => ({
                    ...prev,
                    [questionId]: [...current, value]
                }));
            }
        } else {
            setResponses(prev => ({
                ...prev,
                [questionId]: value
            }));
        }
    };

    const toggleHint = (questionIndex) => {
        setShowHints(prev => ({
            ...prev,
            [questionIndex]: !prev[questionIndex]
        }));
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const validateResponses = () => {
        const errors = {};
        let isValid = true;

        if (form.formType === 'evaluation') {
            form.questions?.forEach((question, idx) => {
                const key = `q${idx}`;
                const answer = responses[key];

                if (question.mandatory) {
                    if (question.type === 'multiple-choice' || question.type === 'mcq') {
                        if (!answer || answer.length === 0) {
                            errors[key] = 'This question is mandatory';
                            isValid = false;
                        }
                    } else if (question.type === 'paragraph') {
                        if (!answer || answer.trim() === '') {
                            errors[key] = 'This question is mandatory';
                            isValid = false;
                        }
                    } else if (['rating', 'nps', 'emoji'].includes(question.type)) {
                        if (!answer || answer === 0) {
                            errors[key] = 'Please provide a rating';
                            isValid = false;
                        }
                    } else {
                        if (!answer) {
                            errors[key] = 'This question is mandatory';
                            isValid = false;
                        }
                    }
                }
            });
        } else if (form.formType === 'feedback') {
            form.questions?.forEach((question, idx) => {
                const key = `q${idx}`;
                const answer = responses[key];

                if (question.required) {
                    if (question.type === 'checkbox') {
                        if (!answer || answer.length === 0) {
                            errors[key] = 'This question is required';
                            isValid = false;
                        }
                    } else if (['text', 'textarea'].includes(question.type)) {
                        if (!answer || answer.trim() === '') {
                            errors[key] = 'This question is required';
                            isValid = false;
                        }
                    } else if (['rating', 'nps', 'emoji'].includes(question.type)) {
                        if (!answer || answer === 0) {
                            errors[key] = 'Please provide a rating';
                            isValid = false;
                        }
                    } else {
                        if (!answer) {
                            errors[key] = 'This question is required';
                            isValid = false;
                        }
                    }
                }
            });
        } else if (form.formType === 'input-detail') {
            form.fields?.forEach((field, idx) => {
                const key = `f${idx}`;
                const answer = responses[key];

                if (field.required) {
                    if (field.fieldType === 'checkbox') {
                        if (!answer || answer.length === 0) {
                            errors[key] = 'This field is required';
                            isValid = false;
                        }
                    } else if (['text', 'textarea', 'email', 'phone', 'url'].includes(field.fieldType)) {
                        if (!answer || answer.trim() === '') {
                            errors[key] = 'This field is required';
                            isValid = false;
                        }
                    } else if (field.fieldType === 'number') {
                        if (answer === '' || answer === null || answer === undefined) {
                            errors[key] = 'This field is required';
                            isValid = false;
                        }
                    } else {
                        if (!answer) {
                            errors[key] = 'This field is required';
                            isValid = false;
                        }
                    }
                }

                // Type-specific validation
                if (answer && answer !== '') {
                    if (field.fieldType === 'email') {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(answer)) {
                            errors[key] = 'Please enter a valid email address';
                            isValid = false;
                        }
                    }

                    if (field.fieldType === 'phone') {
                        const phoneRegex = /^[0-9]{10}$/;
                        if (!phoneRegex.test(answer.replace(/[^0-9]/g, ''))) {
                            errors[key] = 'Please enter a valid 10-digit phone number';
                            isValid = false;
                        }
                    }

                    if (field.fieldType === 'url') {
                        try {
                            new URL(answer);
                        } catch {
                            errors[key] = 'Please enter a valid URL (e.g., https://example.com)';
                            isValid = false;
                        }
                    }

                    if (field.fieldType === 'number') {
                        const num = parseFloat(answer);
                        if (field.validation?.min !== null && num < field.validation.min) {
                            errors[key] = `Minimum value is ${field.validation.min}`;
                            isValid = false;
                        }
                        if (field.validation?.max !== null && num > field.validation.max) {
                            errors[key] = `Maximum value is ${field.validation.max}`;
                            isValid = false;
                        }
                    }

                    if (['text', 'textarea'].includes(field.fieldType)) {
                        if (field.validation?.minLength && answer.length < field.validation.minLength) {
                            errors[key] = `Minimum length is ${field.validation.minLength} characters`;
                            isValid = false;
                        }
                        if (field.validation?.maxLength && answer.length > field.validation.maxLength) {
                            errors[key] = `Maximum length is ${field.validation.maxLength} characters`;
                            isValid = false;
                        }
                    }
                }
            });
        }

        setValidationErrors(errors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateResponses()) {
            setError('Please fix the errors below and try again');
            return;
        }

        try {
            if (!user) {
                setError('User not authenticated');
                return;
            }

            setSubmitting(true);

            const firstName = userProfile?.firstName || 'Student';
            const lastName = userProfile?.lastName || '';
            const studentName = `${firstName} ${lastName}`.trim();

            const cleanResponses = {};
            Object.keys(responses).forEach(key => {
                let value = responses[key];
                if (Array.isArray(value)) {
                    cleanResponses[key] = value.join(', ');
                } else {
                    cleanResponses[key] = value || '';
                }
            });

            const questionScores = {};
            let totalObtainedScore = 0;
            let totalMaxScore = 0;

            // Calculate scores for evaluation forms
            if (form.formType === 'evaluation' && form.questions && form.questions.length > 0) {
                form.questions.forEach((question, idx) => {
                    const qKey = `q${idx}`;
                    const maxScore = question.maxScore || 10;
                    const userAnswer = responses[qKey];
                    totalMaxScore += maxScore;
                    let obtainedScore = 0;

                    if (question.type === 'single-select') {
                        if (userAnswer) {
                            const selectedOption = question.options?.find(opt => opt.text === userAnswer);
                            if (selectedOption) {
                                obtainedScore = selectedOption.score || 0;
                            }
                        }
                    } else if (question.type === 'multiple-choice' || question.type === 'mcq') {
                        if (Array.isArray(userAnswer) && userAnswer.length > 0) {
                            userAnswer.forEach(answer => {
                                const selectedOption = question.options?.find(opt => opt.text === answer);
                                if (selectedOption) {
                                    obtainedScore += selectedOption.score || 0;
                                }
                            });
                        }
                    } else if (question.type === 'true-false') {
                        if (userAnswer) {
                            const selectedOption = question.options?.find(opt => opt.text === userAnswer);
                            if (selectedOption) {
                                obtainedScore = selectedOption.score || 0;
                            }
                        }
                    } else if (question.type === 'paragraph') {
                        if (userAnswer && userAnswer.trim().length > 0) {
                            obtainedScore = maxScore; // Full score for paragraph answers (manual grading needed)
                        }
                    }

                    questionScores[qKey] = {
                        obtained: obtainedScore,
                        maxScore: maxScore,
                        percentage: maxScore > 0 ? Math.round((obtainedScore / maxScore) * 100) : 0
                    };
                    totalObtainedScore += obtainedScore;
                });
            }

            const totalPercentage = totalMaxScore > 0
                ? Math.round((totalObtainedScore / totalMaxScore) * 100)
                : 0;

            let finalStatus = 'Pending';
            if (form.formType === 'evaluation') {
                const passingScore = form.passingScore || 70;
                if (totalPercentage >= passingScore) {
                    finalStatus = 'Admit';
                } else {
                    finalStatus = 'Reject';
                }
            } else {
                finalStatus = 'Submitted';
            }

            const docData = {
                formId: formId,
                firstName: firstName,
                lastName: lastName,
                responses: cleanResponses,
                studentName: studentName,
                questionScores: questionScores,
                totalObtainedScore: totalObtainedScore,
                totalMaxScore: totalMaxScore,
                totalPercentage: totalPercentage,
                finalStatus: finalStatus,
                status: 'submitted',
                submittedAt: serverTimestamp(),
                userId: user.uid,
                userEmail: user.email,
                formType: form.formType,
                timeSpent: form.timeLimit ? (form.timeLimit * 60 - (timeLeft || 0)) : null,
            };

            await addDoc(collection(db, 'submissions'), docData);

            setError('');
            setResponses({});
            setSuccessOpen(true);

            // Redirect after success
            if (form.redirectUrl) {
                setTimeout(() => {
                    window.location.href = form.redirectUrl;
                }, 2000);
            } else {
                setTimeout(() => {
                    setSuccessOpen(false);
                    navigate('/');
                }, 2000);
            }

        } catch (error) {
            console.error('❌ Error saving submission:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography>Loading form...</Typography>
                </Paper>
            </Container>
        );
    }

    if (error && !form) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" gutterBottom>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </Paper>
            </Container>
        );
    }

    if (!form) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Form not found
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </Paper>
            </Container>
        );
    }

    const answeredCount = Object.keys(responses).filter(k => {
        const ans = responses[k];
        return Array.isArray(ans) ? ans.length > 0 : ans && ans !== 0 && ans !== '';
    }).length;

    const totalQuestions = form.formType === 'input-detail'
        ? (form.fields?.length || 0)
        : (form.questions?.length || 0);

    const progress = (answeredCount / totalQuestions) * 100;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {/* Header */}
                <Box
                    sx={{
                        background: form.formType === 'evaluation'
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : form.formType === 'feedback'
                                ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        p: 4
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <img src={logo} alt="Logo" style={{ height: 50, marginRight: 16 }} />
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {form.title}
                        </Typography>
                    </Box>

                    {form.description && (
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                            {form.description}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${totalQuestions} ${form.formType === 'input-detail' ? 'Fields' : 'Questions'}`}
                            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        />

                        {form.formType === 'evaluation' && form.passingScore && (
                            <Chip
                                label={`Passing Score: ${form.passingScore}%`}
                                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                            />
                        )}

                        {form.formType === 'evaluation' && form.timeLimit > 0 && (
                            <Chip
                                icon={<TimerIcon sx={{ color: 'white !important' }} />}
                                label={timeLeft !== null ? formatTime(timeLeft) : `${form.timeLimit} min`}
                                sx={{
                                    backgroundColor: timeLeft < 60 ? 'rgba(255,82,82,0.9)' : 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ px: 4, pt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {answeredCount} / {totalQuestions}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 6, borderRadius: 1 }}
                    />
                </Box>

                {/* Error Alert */}
                {error && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Box>
                )}

                {/* Already Submitted Alert */}
                {alreadySubmitted && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="info" icon={<CheckCircleOutlineIcon />}>
                            You have already submitted this form. Your previous responses are shown below.
                        </Alert>
                    </Box>
                )}

                {/* Form Content */}
                <Box sx={{ p: 4 }}>
                    {/* EVALUATION FORM */}
                    {form.formType === 'evaluation' && (
                        <>
                            {form.questions?.map((question, idx) => (
                                <Card
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                        mb: 3,
                                        borderLeft: `4px solid ${validationErrors[`q${idx}`] ? '#f44336' :
                                                question.difficulty === 'easy' ? '#4caf50' :
                                                    question.difficulty === 'hard' ? '#ff9800' : '#2196f3'
                                            }`
                                    }}
                                >
                                    <CardContent>
                                        {/* Question Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" sx={{ mb: 1 }}>
                                                    {idx + 1}. {question.text}
                                                    {question.mandatory && (
                                                        <Chip
                                                            label="Required"
                                                            size="small"
                                                            color="error"
                                                            sx={{ ml: 1, height: 20 }}
                                                        />
                                                    )}
                                                </Typography>

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {question.category && (
                                                        <Chip
                                                            label={question.category}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {question.difficulty && (
                                                        <Chip
                                                            label={question.difficulty}
                                                            size="small"
                                                            color={
                                                                question.difficulty === 'easy' ? 'success' :
                                                                    question.difficulty === 'hard' ? 'warning' : 'info'
                                                            }
                                                        />
                                                    )}
                                                    <Chip
                                                        label={`${question.maxScore || 10} points`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </Box>
                                            </Box>

                                            {question.hint && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleHint(idx)}
                                                    color="primary"
                                                >
                                                    <HintIcon />
                                                </IconButton>
                                            )}
                                        </Box>

                                        {/* Hint */}
                                        {question.hint && (
                                            <Collapse in={showHints[idx]}>
                                                <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 2 }}>
                                                    <strong>Hint:</strong> {question.hint}
                                                </Alert>
                                            </Collapse>
                                        )}

                                        {/* Validation Error */}
                                        {validationErrors[`q${idx}`] && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {validationErrors[`q${idx}`]}
                                            </Alert>
                                        )}

                                        <Divider sx={{ mb: 2 }} />

                                        {/* Single Select */}
                                        {question.type === 'single-select' && (
                                            <RadioGroup
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'single-select')}
                                            >
                                                {question.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        value={opt.text}
                                                        control={<Radio disabled={alreadySubmitted} />}
                                                        label={
                                                            <Box>
                                                                <Typography>{opt.text}</Typography>
                                                                {opt.score > 0 && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ({opt.score} points)
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        {/* Multiple Choice */}
                                        {(question.type === 'multiple-choice' || question.type === 'mcq') && (
                                            <FormGroup>
                                                {question.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        control={
                                                            <Checkbox
                                                                checked={(responses[`q${idx}`] || []).includes(opt.text)}
                                                                onChange={() => handleResponseChange(`q${idx}`, opt.text, 'multiple-choice')}
                                                                disabled={alreadySubmitted}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography>{opt.text}</Typography>
                                                                {opt.score > 0 && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ({opt.score} points)
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}

                                        {/* True/False */}
                                        {question.type === 'true-false' && (
                                            <RadioGroup
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'true-false')}
                                                row
                                            >
                                                {question.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        value={opt.text}
                                                        control={<Radio disabled={alreadySubmitted} />}
                                                        label={opt.text}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mr: 2,
                                                            px: 3,
                                                            py: 1
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        {/* Paragraph */}
                                        {question.type === 'paragraph' && (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'paragraph')}
                                                placeholder="Type your answer here..."
                                                variant="outlined"
                                                error={!!validationErrors[`q${idx}`]}
                                                disabled={alreadySubmitted}
                                            />
                                        )}

                                        {/* Explanation */}
                                        {question.explanation && (
                                            <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Note:</strong> {question.explanation}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}

                    {/* FEEDBACK FORM */}
                    {form.formType === 'feedback' && (
                        <>
                            {form.questions?.map((question, idx) => (
                                <Card
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                        mb: 3,
                                        borderLeft: `4px solid ${validationErrors[`q${idx}`] ? '#f44336' : '#4facfe'}`
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>
                                            {idx + 1}. {question.text}
                                            {question.required && (
                                                <Chip
                                                    label="Required"
                                                    size="small"
                                                    color="warning"
                                                    sx={{ ml: 1, height: 20 }}
                                                />
                                            )}
                                        </Typography>

                                        {validationErrors[`q${idx}`] && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {validationErrors[`q${idx}`]}
                                            </Alert>
                                        )}

                                        <Divider sx={{ mb: 3 }} />

                                        {/* Rating */}
                                        {question.type === 'rating' && (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    <Rating
                                                        value={responses[`q${idx}`] || 0}
                                                        max={question.scale || 5}
                                                        onChange={(e, value) => handleResponseChange(`q${idx}`, value, 'rating')}
                                                        size="large"
                                                        disabled={alreadySubmitted}
                                                        sx={{ fontSize: '3rem' }}
                                                    />
                                                    {responses[`q${idx}`] > 0 && (
                                                        <Typography variant="h6" color="primary">
                                                            {responses[`q${idx}`]} / {question.scale || 5}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {question.labels && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {question.labels.min}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {question.labels.max}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {/* NPS Score */}
                                        {question.type === 'nps' && (
                                            <Box>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                    {[...Array(11)].map((_, i) => (
                                                        <Button
                                                            key={i}
                                                            variant={responses[`q${idx}`] === i ? 'contained' : 'outlined'}
                                                            onClick={() => handleResponseChange(`q${idx}`, i, 'nps')}
                                                            disabled={alreadySubmitted}
                                                            sx={{
                                                                minWidth: '50px',
                                                                backgroundColor: responses[`q${idx}`] === i ?
                                                                    (i <= 6 ? '#f44336' : i <= 8 ? '#ff9800' : '#4caf50') :
                                                                    'transparent',
                                                                color: responses[`q${idx}`] === i ? 'white' : 'inherit',
                                                                borderColor: i <= 6 ? '#f44336' : i <= 8 ? '#ff9800' : '#4caf50',
                                                            }}
                                                        >
                                                            {i}
                                                        </Button>
                                                    ))}
                                                </Box>
                                                {question.labels && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {question.labels.min || 'Not at all likely'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {question.labels.max || 'Extremely likely'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {/* Emoji Rating */}
                                        {question.type === 'emoji' && (
                                            <Box>
                                                <Rating
                                                    value={responses[`q${idx}`] || 0}
                                                    onChange={(e, value) => handleResponseChange(`q${idx}`, value, 'emoji')}
                                                    IconContainerComponent={IconContainer}
                                                    getLabelText={(value) => emojiIcons[value].label}
                                                    highlightSelectedOnly
                                                    size="large"
                                                    disabled={alreadySubmitted}
                                                    sx={{ fontSize: '3rem' }}
                                                />
                                                {responses[`q${idx}`] > 0 && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                        {emojiIcons[responses[`q${idx}`]]?.label}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        {/* Short Text */}
                                        {question.type === 'text' && (
                                            <TextField
                                                fullWidth
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'text')}
                                                placeholder={question.placeholder || 'Type your answer...'}
                                                variant="outlined"
                                                error={!!validationErrors[`q${idx}`]}
                                                disabled={alreadySubmitted}
                                                inputProps={{ maxLength: question.maxLength || 500 }}
                                                helperText={`${(responses[`q${idx}`] || '').length} / ${question.maxLength || 500}`}
                                            />
                                        )}

                                        {/* Long Text */}
                                        {question.type === 'textarea' && (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={question.rows || 3}
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'textarea')}
                                                placeholder={question.placeholder || 'Type your feedback...'}
                                                variant="outlined"
                                                error={!!validationErrors[`q${idx}`]}
                                                disabled={alreadySubmitted}
                                                inputProps={{ maxLength: question.maxLength || 500 }}
                                                helperText={`${(responses[`q${idx}`] || '').length} / ${question.maxLength || 500}`}
                                            />
                                        )}

                                        {/* Radio */}
                                        {question.type === 'radio' && (
                                            <RadioGroup
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'radio')}
                                            >
                                                {question.options?.map((option, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        value={option}
                                                        control={<Radio disabled={alreadySubmitted} />}
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        {/* Checkbox */}
                                        {question.type === 'checkbox' && (
                                            <FormGroup>
                                                {question.options?.map((option, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        control={
                                                            <Checkbox
                                                                checked={(responses[`q${idx}`] || []).includes(option)}
                                                                onChange={() => handleResponseChange(`q${idx}`, option, 'checkbox')}
                                                                disabled={alreadySubmitted}
                                                            />
                                                        }
                                                        label={option}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}

                                        {/* Dropdown */}
                                        {question.type === 'dropdown' && (
                                            <FormControl fullWidth error={!!validationErrors[`q${idx}`]}>
                                                <Select
                                                    value={responses[`q${idx}`] || ''}
                                                    onChange={(e) => handleResponseChange(`q${idx}`, e.target.value, 'dropdown')}
                                                    disabled={alreadySubmitted}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="" disabled>
                                                        {question.placeholder || 'Select an option...'}
                                                    </MenuItem>
                                                    {question.options?.map((option, optIdx) => (
                                                        <MenuItem key={optIdx} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}

                    {/* INPUT DETAIL FORM */}
                    {form.formType === 'input-detail' && (
                        <>
                            {form.fields?.map((field, idx) => (
                                <Card
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                        mb: 3,
                                        borderLeft: `4px solid ${validationErrors[`f${idx}`] ? '#f44336' : '#f093fb'}`
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            {field.label}
                                            {field.required && (
                                                <Chip
                                                    label="Required"
                                                    size="small"
                                                    color="error"
                                                    sx={{ ml: 1, height: 20 }}
                                                />
                                            )}
                                        </Typography>

                                        {field.helpText && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                💡 {field.helpText}
                                            </Typography>
                                        )}

                                        {validationErrors[`f${idx}`] && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {validationErrors[`f${idx}`]}
                                            </Alert>
                                        )}

                                        <Divider sx={{ mb: 2 }} />

                                        {/* Text */}
                                        {field.fieldType === 'text' && (
                                            <TextField
                                                fullWidth
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'text')}
                                                placeholder={field.placeholder || 'Enter text...'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                inputProps={{
                                                    minLength: field.validation?.minLength,
                                                    maxLength: field.validation?.maxLength,
                                                }}
                                                helperText={
                                                    field.validation?.maxLength
                                                        ? `${(responses[`f${idx}`] || '').length} / ${field.validation.maxLength}`
                                                        : ''
                                                }
                                            />
                                        )}

                                        {/* Textarea */}
                                        {field.fieldType === 'textarea' && (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={field.rows || 4}
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'textarea')}
                                                placeholder={field.placeholder || 'Enter detailed text...'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                inputProps={{
                                                    minLength: field.validation?.minLength,
                                                    maxLength: field.validation?.maxLength,
                                                }}
                                                helperText={
                                                    field.validation?.maxLength
                                                        ? `${(responses[`f${idx}`] || '').length} / ${field.validation.maxLength}`
                                                        : ''
                                                }
                                            />
                                        )}

                                        {/* Email */}
                                        {field.fieldType === 'email' && (
                                            <TextField
                                                fullWidth
                                                type="email"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'email')}
                                                placeholder={field.placeholder || 'example@email.com'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">📧</InputAdornment>,
                                                }}
                                            />
                                        )}

                                        {/* Phone */}
                                        {field.fieldType === 'phone' && (
                                            <TextField
                                                fullWidth
                                                type="tel"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'phone')}
                                                placeholder={field.placeholder || '9876543210'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">📱</InputAdornment>,
                                                }}
                                                helperText="Enter 10-digit phone number"
                                            />
                                        )}

                                        {/* URL */}
                                        {field.fieldType === 'url' && (
                                            <TextField
                                                fullWidth
                                                type="url"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'url')}
                                                placeholder={field.placeholder || 'https://example.com'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">🌐</InputAdornment>,
                                                }}
                                            />
                                        )}

                                        {/* Number */}
                                        {field.fieldType === 'number' && (
                                            <TextField
                                                fullWidth
                                                type="number"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'number')}
                                                placeholder={field.placeholder || 'Enter number...'}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                inputProps={{
                                                    min: field.validation?.min,
                                                    max: field.validation?.max,
                                                }}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">🔢</InputAdornment>,
                                                }}
                                            />
                                        )}

                                        {/* Date */}
                                        {field.fieldType === 'date' && (
                                            <TextField
                                                fullWidth
                                                type="date"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'date')}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}

                                        {/* Time */}
                                        {field.fieldType === 'time' && (
                                            <TextField
                                                fullWidth
                                                type="time"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'time')}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}

                                        {/* DateTime */}
                                        {field.fieldType === 'datetime' && (
                                            <TextField
                                                fullWidth
                                                type="datetime-local"
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'datetime')}
                                                variant="outlined"
                                                error={!!validationErrors[`f${idx}`]}
                                                disabled={alreadySubmitted}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}

                                        {/* Dropdown */}
                                        {field.fieldType === 'dropdown' && (
                                            <FormControl fullWidth error={!!validationErrors[`f${idx}`]}>
                                                <Select
                                                    value={responses[`f${idx}`] || ''}
                                                    onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'dropdown')}
                                                    disabled={alreadySubmitted}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="" disabled>
                                                        {field.placeholder || 'Select an option...'}
                                                    </MenuItem>
                                                    {field.options?.map((option, optIdx) => (
                                                        <MenuItem
                                                            key={optIdx}
                                                            value={typeof option === 'object' ? option.value : option}
                                                        >
                                                            {typeof option === 'object' ? option.label : option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {/* Radio */}
                                        {field.fieldType === 'radio' && (
                                            <RadioGroup
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) => handleResponseChange(`f${idx}`, e.target.value, 'radio')}
                                            >
                                                {field.options?.map((option, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        value={typeof option === 'object' ? option.value : option}
                                                        control={<Radio disabled={alreadySubmitted} />}
                                                        label={typeof option === 'object' ? option.label : option}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        {/* Checkbox */}
                                        {field.fieldType === 'checkbox' && (
                                            <FormGroup>
                                                {field.options?.map((option, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        control={
                                                            <Checkbox
                                                                checked={(responses[`f${idx}`] || []).includes(
                                                                    typeof option === 'object' ? option.value : option
                                                                )}
                                                                onChange={() =>
                                                                    handleResponseChange(
                                                                        `f${idx}`,
                                                                        typeof option === 'object' ? option.value : option,
                                                                        'checkbox'
                                                                    )
                                                                }
                                                                disabled={alreadySubmitted}
                                                            />
                                                        }
                                                        label={typeof option === 'object' ? option.label : option}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1,
                                                        }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}

                                        {/* File Upload */}
                                        {field.fieldType === 'file' && (
                                            <Box>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<UploadIcon />}
                                                    fullWidth
                                                    disabled={alreadySubmitted}
                                                >
                                                    Choose File
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept={field.validation?.accept}
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                handleResponseChange(`f${idx}`, file.name, 'file');
                                                            }
                                                        }}
                                                    />
                                                </Button>
                                                {responses[`f${idx}`] && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                        Selected: {responses[`f${idx}`]}
                                                    </Typography>
                                                )}
                                                {field.validation?.accept && (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                        Accepted formats: {field.validation.accept}
                                                    </Typography>
                                                )}
                                                {field.validation?.maxSize && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Max size: {field.validation.maxSize}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(-1)}
                            disabled={submitting}
                            sx={{ px: 4, py: 1.2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || alreadySubmitted}
                            sx={{
                                px: 6,
                                py: 1.5,
                                fontSize: '1.1rem',
                                background:
                                    form.formType === 'evaluation'
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : form.formType === 'feedback'
                                            ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                                            : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            }}
                        >
                            {submitting ? 'Submitting...' : alreadySubmitted ? 'Already Submitted' : 'Submit Form'}
                        </Button>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" color="text.secondary">
                        © 2024 Form Submission Portal. All rights reserved.
                    </Typography>
                </Box>
            </Paper>

            {/* Success Dialog */}
            <Dialog open={successOpen} maxWidth="xs" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Form Submitted Successfully
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Redirecting to home...
                    </Typography>
                    <LinearProgress sx={{ mt: 3 }} />
                </DialogContent>
            </Dialog>
        </Container>
    );
}
